import React from 'react';

/**
 * QuotaBanner Component
 * Displays quota usage in a card format with progress bar
 * Matches the Monthly Usage card design with 3 states: ok, warning, exceeded
 * Responsive: adapts to container width
 */
const QuotaBanner = ({
  patientMonths = 0,
  limit = null,
  quotaStatus = 'no_quota',
  onClick = null
}) => {
  // Calculate derived values (handle limit=0 case)
  const remaining = (limit !== null && limit !== undefined) ? Math.max(0, limit - patientMonths) : 0;
  const utilizationPercent = limit > 0 ? Math.round((patientMonths / limit) * 100) : (patientMonths > 0 ? 100 : 0);
  const isExceeded = quotaStatus === 'exceeded';
  const overLimit = isExceeded ? patientMonths - limit : 0;

  // Color schemes based on status
  const getColors = () => {
    switch (quotaStatus) {
      case 'ok':
        return {
          primary: '#3b82f6', // blue
          usedColor: '#3b82f6', // blue for USED icon/label
          balanceColor: '#6b7280', // gray for BALANCE icon/label
          progressBg: '#e5e7eb'
        };
      case 'warning':
        return {
          primary: '#f59e0b', // orange
          usedColor: '#f59e0b', // orange for USED icon/label
          balanceColor: '#6b7280', // gray for BALANCE icon/label
          progressBg: '#e5e7eb'
        };
      case 'exceeded':
        return {
          primary: '#dc2626', // red for progress bar and icons
          usedColor: '#dc2626', // red for USED icon/label in exceeded state
          balanceColor: '#dc2626', // red for BALANCE icon/label in exceeded state
          progressBg: '#fecaca'
        };
      default:
        return {
          primary: '#9ca3af',
          usedColor: '#9ca3af',
          balanceColor: '#6b7280',
          progressBg: '#e5e7eb'
        };
    }
  };

  const colors = getColors();

  // Don't render if no quota set (limit can be 0 and should still display)
  if (quotaStatus === 'no_quota' || limit === null || limit === undefined) {
    return null;
  }

  return (
    <div
      style={{
        background: 'white',
        borderRadius: 'clamp(8px, 1.5vw, 12px)',
        border: `2px solid ${isExceeded ? '#f5c6c6' : '#e5e7eb'}`,
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
        padding: '0',
        width: '100%',
        maxWidth: '900px',
        minWidth: '280px',
        boxSizing: 'border-box',
        cursor: onClick ? 'pointer' : 'default',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
      }}
      onClick={onClick}
    >
      {/* Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: 'clamp(12px, 2vw, 16px) clamp(16px, 3vw, 24px)',
        borderBottom: `1px solid ${isExceeded ? '#f5c6c6' : '#e5e7eb'}`,
        background: isExceeded ? '#fefafa' : 'white',
        borderRadius: 'clamp(6px, 1.2vw, 10px) clamp(6px, 1.2vw, 10px) 0 0',
        flexWrap: 'wrap',
        gap: 'clamp(6px, 1vw, 8px)'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'clamp(8px, 1.5vw, 12px)' }}>
          {/* Calendar/Card Icon - always red */}
          <svg style={{ width: 'clamp(18px, 2.5vw, 22px)', height: 'clamp(18px, 2.5vw, 22px)' }} viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2">
            <rect x="3" y="4" width="18" height="16" rx="2" />
            <line x1="3" y1="10" x2="21" y2="10" />
          </svg>
          <span style={{
            fontSize: 'clamp(12px, 1.8vw, 15px)',
            fontWeight: '600',
            color: '#1f2937',
            letterSpacing: '0.5px'
          }}>
            CREDITS
          </span>
        </div>
        {isExceeded && (
          <span style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 'clamp(5px, 1vw, 8px)',
            padding: 'clamp(6px, 1vw, 8px) clamp(12px, 2vw, 18px)',
            background: '#fef2f2',
            border: '1px solid #fca5a5',
            borderRadius: 'clamp(16px, 3vw, 24px)',
            fontSize: 'clamp(11px, 1.5vw, 13px)',
            fontWeight: '600',
            color: '#dc2626',
            letterSpacing: '0.5px'
          }}>
            <svg style={{ width: 'clamp(12px, 1.8vw, 16px)', height: 'clamp(12px, 1.8vw, 16px)' }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <circle cx="12" cy="16" r="1" fill="currentColor" />
            </svg>
            EXCEEDED
          </span>
        )}
      </div>

      {/* Stats Row */}
      <div style={{
        display: 'flex',
        padding: 'clamp(16px, 3vw, 24px) 0'
      }}>
        {/* USED Section */}
        <div style={{
          width: '50%',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          paddingLeft: 'clamp(16px, 3vw, 24px)',
          paddingRight: 'clamp(16px, 3vw, 24px)',
          borderRight: '1px solid #e5e7eb',
          boxSizing: 'border-box'
        }}>
          {/* Left side - labels */}
          <div>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'clamp(4px, 0.8vw, 6px)',
              marginBottom: 'clamp(2px, 0.5vw, 4px)'
            }}>
              {/* Trend icon - GRAY */}
              <svg style={{ width: 'clamp(12px, 1.8vw, 16px)', height: 'clamp(12px, 1.8vw, 16px)' }} viewBox="0 0 24 24" fill="none" stroke="#6b7280" strokeWidth="2.5">
                <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" />
                <polyline points="17 6 23 6 23 12" />
              </svg>
              <span style={{
                fontSize: 'clamp(10px, 1.5vw, 13px)',
                fontWeight: '600',
                color: '#6b7280',
                letterSpacing: '0.5px'
              }}>
                USED
              </span>
            </div>
            <div style={{
              fontSize: 'clamp(12px, 1.8vw, 16px)',
              color: '#1f2937',
              fontWeight: '400'
            }}>
              credits
            </div>
          </div>
          {/* Right side - numbers */}
          <div style={{ textAlign: 'right' }}>
            <div style={{
              fontSize: 'clamp(32px, 5.5vw, 48px)',
              fontWeight: '700',
              color: '#111827',
              lineHeight: '1',
              letterSpacing: '-1px'
            }}>
              {patientMonths.toLocaleString()}
            </div>
            <div style={{
              fontSize: 'clamp(11px, 1.7vw, 15px)',
              color: '#9ca3af',
              marginTop: 'clamp(4px, 0.7vw, 6px)',
              fontWeight: '400'
            }}>
              of {limit.toLocaleString()}
            </div>
          </div>
        </div>

        {/* BALANCE Section */}
        <div style={{
          width: '50%',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          paddingLeft: 'clamp(16px, 3vw, 24px)',
          paddingRight: 'clamp(16px, 3vw, 24px)',
          boxSizing: 'border-box'
        }}>
          {/* Left side - labels */}
          <div>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'clamp(4px, 0.8vw, 6px)',
              marginBottom: 'clamp(2px, 0.5vw, 4px)'
            }}>
              {/* Check icon - GRAY */}
              <svg style={{ width: 'clamp(12px, 1.8vw, 16px)', height: 'clamp(12px, 1.8vw, 16px)' }} viewBox="0 0 24 24" fill="none" stroke="#6b7280" strokeWidth="2">
                <circle cx="12" cy="12" r="10" />
                <polyline points="9 12 11 14 15 10" />
              </svg>
              <span style={{
                fontSize: 'clamp(10px, 1.5vw, 13px)',
                fontWeight: '600',
                color: '#6b7280',
                letterSpacing: '0.5px'
              }}>
                BALANCE
              </span>
            </div>
            <div style={{
              fontSize: 'clamp(12px, 1.8vw, 16px)',
              color: '#1f2937',
              fontWeight: '400'
            }}>
              credits
            </div>
          </div>
          {/* Right side - numbers */}
          <div style={{ textAlign: 'right' }}>
            <div style={{
              fontSize: 'clamp(32px, 5.5vw, 48px)',
              fontWeight: '700',
              color: isExceeded ? '#dc2626' : '#111827',
              lineHeight: '1',
              letterSpacing: '-1px'
            }}>
              {isExceeded ? `-${overLimit.toLocaleString()}` : remaining.toLocaleString()}
            </div>
            <div style={{
              fontSize: 'clamp(11px, 1.7vw, 15px)',
              color: isExceeded ? '#dc2626' : '#9ca3af',
              marginTop: 'clamp(4px, 0.7vw, 6px)',
              fontWeight: '400',
              whiteSpace: 'nowrap'
            }}>
              {isExceeded ? 'over limit' : 'available'}
            </div>
          </div>
        </div>
      </div>

      {/* Progress Bar */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: 'clamp(10px, 2vw, 16px)',
        padding: 'clamp(12px, 2vw, 16px) clamp(16px, 3vw, 24px) clamp(14px, 2.5vw, 20px) clamp(16px, 3vw, 24px)',
        borderTop: '1px solid #e5e7eb'
      }}>
        <div style={{
          flex: 1,
          height: 'clamp(6px, 1.2vw, 10px)',
          background: isExceeded ? '#fecaca' : '#e5e7eb',
          borderRadius: 'clamp(3px, 0.6vw, 5px)',
          overflow: 'hidden'
        }}>
          <div style={{
            width: `${Math.min(utilizationPercent, 100)}%`,
            height: '100%',
            background: isExceeded ? '#dc2626' : colors.primary,
            borderRadius: 'clamp(3px, 0.6vw, 5px)',
            transition: 'width 0.3s ease'
          }} />
        </div>
        <span style={{
          fontSize: 'clamp(12px, 1.7vw, 15px)',
          fontWeight: '600',
          color: isExceeded ? '#dc2626' : '#6b7280',
          minWidth: 'clamp(40px, 6vw, 50px)',
          textAlign: 'right'
        }}>
          {utilizationPercent}%
        </span>
      </div>
    </div>
  );
};

export default QuotaBanner;
